import { CharacterState } from "../CharacterStore.State";
import { CharacterAdjustMovement, CharacterOverrideMovement, CharacterPendingProperties, CharacterPendingProperty, CharacterSaveProperties, CharacterSaveProperty, CharacterSetJuiced, CharacterSetRuleEnforcement, CharacterUpdateClassName, CharacterUpdateHealth, CharacterUpdateKinship, CharacterUpdateLevel, CharacterUpdateMoney, CharacterUpdateName } from "../actions/CharacterPropertyActions";
import { CharacterReducerHelpers } from "./CharacterReducerHelpers";

const setRuleEnforcement = (state: CharacterState, action: CharacterSetRuleEnforcement): CharacterState => {
    const { characterId, enforceRules } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, enforceRules };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateName = (state: CharacterState, action: CharacterUpdateName): CharacterState => {
    const { characterId, name } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, name };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateKinship = (state: CharacterState, action: CharacterUpdateKinship): CharacterState => {
    const { characterId, kinship } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, kinship };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateLevel = (state: CharacterState, action: CharacterUpdateLevel): CharacterState => {
    const { characterId, level } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, level };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateClassName = (state: CharacterState, action: CharacterUpdateClassName): CharacterState => {
    const { characterId, className } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, className };

    const characters = [...state.characters.filter(c => c.id != newCharacter.id), character];
    return { ...state, characters };
}

const adjustMovement = (state: CharacterState, action: CharacterAdjustMovement): CharacterState => {
    const { characterId, movementAdjust } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, movementAdjust };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const overrideMovement = (state: CharacterState, action: CharacterOverrideMovement): CharacterState => {
    const { characterId, movementOverride } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, movementOverride };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateHealth = (state: CharacterState, action: CharacterUpdateHealth): CharacterState => {
    const { characterId, currentHealth } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, currentHealth };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setJuiced = (state: CharacterState, action: CharacterSetJuiced): CharacterState => {
    const { characterId, juiced } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, juiced };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const updateMoney = (state: CharacterState, action: CharacterUpdateMoney): CharacterState => {
    const { characterId, money } = action;
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }

    const newCharacter = { ...character, money };

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setPendingPropertiesShared = (state: CharacterState, characterId: string, properties: string[]): CharacterState => {
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const newCharacter = { ...character, pendingProperties: { ...character.pendingProperties} };
    for (let property of properties) {
        newCharacter.pendingProperties[property] = true;
    }

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setPendingProperties = (state: CharacterState, action: CharacterPendingProperties): CharacterState =>
    setPendingPropertiesShared(state, action.characterId, action.propertyNames);

const setPendingProperty = (state: CharacterState, action: CharacterPendingProperty): CharacterState  =>
    setPendingPropertiesShared(state, action.characterId, [action.propertyName]);


const setSavedPropertiesShared = (state: CharacterState, characterId: string, properties: string[]): CharacterState => {
    const character = CharacterReducerHelpers.getCharacter(state, characterId);
    if (character == null) {
        return state;
    }
    
    const newCharacter = { ...character, pendingProperties: { ...character.pendingProperties} };
    for (let property of properties) {
        delete newCharacter.pendingProperties[property];
    }

    return CharacterReducerHelpers.updateCharacter(state, newCharacter);
}

const setSavedProperties = (state: CharacterState, action: CharacterSaveProperties): CharacterState =>
    setSavedPropertiesShared(state, action.characterId, action.propertyNames);

const setSavedProperty = (state: CharacterState, action: CharacterSaveProperty): CharacterState =>
    setSavedPropertiesShared(state, action.characterId, [action.propertyName]);


export const CharacterPropertyReducers = {
    updateName,
    updateKinship,
    updateLevel,
    updateClassName,
    adjustMovement,
    overrideMovement,
    updateHealth,
    setJuiced,
    updateMoney,
    setPendingProperties,
    setPendingProperty,
    setSavedProperties,
    setSavedProperty,
    setRuleEnforcement,
}